import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart, useCheckout, useValidateCoupon } from '../hooks/api-hooks';
import { Empty, ErrorState, Loading } from '../components/States';
import { formatPrice } from '../lib/format';
import type { CouponValidation } from '../lib/types';

export function CheckoutPage() {
  const paymentsEnabled = import.meta.env.VITE_PAYMENTS_ENABLED !== 'false';
  const cart = useCart();
  const validateCoupon = useValidateCoupon();
  const checkout = useCheckout();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<CouponValidation | null>(null);

  if (cart.isPending) return <Loading />;
  if (cart.error) return <ErrorState error={cart.error} />;

  if (!paymentsEnabled) {
    return (
      <section aria-labelledby="checkout-title">
        <h1 id="checkout-title">Thanh toán chưa mở</h1>
        <div className="notice">
          Đây là phiên bản demo. Anh có thể xem khóa học, skill, workshop và khu vực quản trị;
          hệ thống chưa nhận đơn hàng hoặc chuyển khoản.
        </div>
        <Link to="/">Quay lại trang chủ</Link>
      </section>
    );
  }

  const items = cart.data?.items ?? [];
  const subtotal = cart.data?.subtotal ?? 0;
  // Preview only — never used as the amount to pay.
  const previewDiscount = appliedCoupon?.discountAmount ?? 0;

  if (items.length === 0) {
    return (
      <>
        <h1>Thanh toán</h1>
        <Empty>
          Giỏ hàng đang trống. <Link to="/">Chọn khoá học hoặc skill</Link> trước khi thanh toán.
        </Empty>
      </>
    );
  }

  function applyCoupon(event: FormEvent) {
    event.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) return;
    setAppliedCoupon(null);
    validateCoupon.mutate(trimmed, {
      onSuccess: (coupon) => {
        setAppliedCoupon(coupon);
        setCode(coupon.code);
      },
    });
  }

  function changeCoupon(nextCode: string) {
    setCode(nextCode);
    setAppliedCoupon(null);
    validateCoupon.reset();
  }

  function removeCoupon() {
    setCode('');
    setAppliedCoupon(null);
    validateCoupon.reset();
  }

  function placeOrder() {
    // Send the coupon only when the backend confirmed it.
    if (checkout.isPending || validateCoupon.isPending) return;
    const confirmed = appliedCoupon?.code;
    checkout.mutate(confirmed, {
      onSuccess: (result) =>
        navigate(`/don-hang/${result.order.id}`, { state: { payment: result.payment } }),
    });
  }

  return (
    <section aria-labelledby="checkout-title">
      <h1 id="checkout-title">Thanh toán</h1>
      <p className="muted">Đơn hàng chỉ được xác nhận sau khi hệ thống nhận được chuyển khoản.</p>

      <ul className="list" aria-label="Sản phẩm sẽ thanh toán">
        {items.map((item) => (
          <li key={item.id}>
            <span>{item.product.title}</span>
            <span>{formatPrice(item.lineTotal)}</span>
          </li>
        ))}
      </ul>

      <form onSubmit={applyCoupon} className="inline-form">
        <label htmlFor="coupon">Mã giảm giá (chỉ áp dụng cho khoá học)</label>
        <input
          id="coupon"
          value={code}
          onChange={(event) => changeCoupon(event.target.value)}
          autoComplete="off"
          spellCheck={false}
          disabled={validateCoupon.isPending || checkout.isPending}
          aria-describedby="coupon-note"
        />
        <button
          type="submit"
          disabled={validateCoupon.isPending || checkout.isPending || !code.trim()}
        >
          {validateCoupon.isPending ? 'Đang kiểm tra…' : 'Áp dụng'}
        </button>
      </form>
      <p id="coupon-note" className="muted">
        Coupon chỉ giảm giá cho những khoá học được áp dụng; skill vẫn giữ nguyên giá.
      </p>
      {validateCoupon.error && <ErrorState error={validateCoupon.error} />}
      {appliedCoupon && (
        <div className="notice" role="status" aria-live="polite">
          Mã <strong>{appliedCoupon.code}</strong> hợp lệ — dự kiến giảm{' '}
          {formatPrice(previewDiscount)}.{' '}
          <button type="button" className="link-button" onClick={removeCoupon}>
            Bỏ mã
          </button>
        </div>
      )}

      <p className="muted">Tạm tính: {formatPrice(subtotal)}</p>
      <p className="price">Dự kiến: {formatPrice(Math.max(0, subtotal - previewDiscount))}</p>
      <p className="muted">
        Số tiền chuyển khoản chính thức sẽ do máy chủ tính lại và hiển thị ở bước tiếp theo.
      </p>

      <button
        type="button"
        onClick={placeOrder}
        disabled={checkout.isPending || validateCoupon.isPending}
      >
        {checkout.isPending ? 'Đang tạo đơn…' : 'Tạo đơn hàng'}
      </button>
      {checkout.error && <ErrorState error={checkout.error} />}
    </section>
  );
}
